import type { SupabaseClient } from '@supabase/supabase-js';
import type { IndicatorDoc } from '@core/sim/indicators/dsl/schema';
import { CURRENT_SUPPORTED_INDICATOR_FEATURES, supportsIndicatorFeatures } from '@core/sim/indicators/dsl/marketFeatures';
import { supabase as defaultClient } from '../lib/supabase';
import { E2E_MOCK } from '../lib/e2eMock';
import { apiFetch } from '../lib/api';
import type { PublishCopyViolation } from './indicatorPublishGate';

export type IndicatorPublishStats = { inputs: number; nodes: number; plots: number; maxLookback: number };

export type IndicatorGalleryItem = {
  version_id: string;
  indicator_id: string;
  author_id: string;
  author_nick: string;
  author_title: string | null;
  name: string;
  description: string | null;
  pane: 'main' | 'sub';
  source: 'template' | 'ai' | 'studio';
  doc: IndicatorDoc;
  features: string[];
  doc_hash: string | null;
  stats: IndicatorPublishStats;
  status: 'public' | 'unlisted' | 'blocked';
  install_count: number;
  retained_installs: number;
  review_use_count: number;
  published_at: string;
};

export type GallerySort = 'recent' | 'popular';
export type GalleryFetchImpl = (request: { kind: 'list' | 'detail'; sort?: GallerySort; versionId?: string; supportedFeatures?: readonly string[] }) => Promise<IndicatorGalleryItem[] | IndicatorGalleryItem | null>;
type Options = { client?: SupabaseClient; fetchImpl?: GalleryFetchImpl; supportedFeatures?: readonly string[] };

export const gallerySelect = 'version_id, indicator_id, author_id, author_nick, author_title, name, description, pane, source, doc, features, doc_hash, stats, status, install_count, retained_installs, review_use_count, published_at';

export async function fetchIndicatorGallery(sort: GallerySort = 'recent', options: Options = {}): Promise<IndicatorGalleryItem[]> {
  if (E2E_MOCK) return [];
  const supportedFeatures = options.supportedFeatures ?? CURRENT_SUPPORTED_INDICATOR_FEATURES;
  if (options.fetchImpl) {
    const items = (await options.fetchImpl({ kind: 'list', sort, supportedFeatures }) as IndicatorGalleryItem[]) ?? [];
    return items.filter((item) => supportsIndicatorFeatures(item.features ?? [], supportedFeatures));
  }
  const query = new URLSearchParams({ sort, supportedFeatures: supportedFeatures.join(',') });
  const response = await apiFetch<{ items: IndicatorGalleryItem[] }>(`/api/mobile/indicator-gallery?${query.toString()}`, { cache: 'no-store' });
  return (response.items ?? []).filter((item) => supportsIndicatorFeatures(item.features ?? [], supportedFeatures));
}

export async function fetchIndicatorDetail(versionId: string, options: Options = {}): Promise<IndicatorGalleryItem | null> {
  if (E2E_MOCK) return null;
  if (options.fetchImpl) return await options.fetchImpl({ kind: 'detail', versionId }) as IndicatorGalleryItem | null;
  const client: SupabaseClient = options.client ?? defaultClient;
  const { data, error } = await client.from('indicator_versions').select(gallerySelect).eq('version_id', versionId).eq('status', 'public').maybeSingle();
  if (error) throw error;
  return data as IndicatorGalleryItem | null;
}

// 이번 주(최근 7일) 설치 최다 지표 1건 — 서버 RPC 계산(누적순 아님). 홈 크로스카드용(X1 T-05)
export async function fetchWeeklyPopularIndicator(client: SupabaseClient = defaultClient): Promise<IndicatorGalleryItem | null> {
  if (E2E_MOCK) return null;
  const { data, error } = await client.rpc('get_compatible_weekly_popular_indicator', { p_supported_features: [...CURRENT_SUPPORTED_INDICATOR_FEATURES] });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return (row as IndicatorGalleryItem) ?? null;
}

export async function publishIndicator(localId: string, description: string, indicatorId?: string, client: SupabaseClient = defaultClient): Promise<string> {
  if (E2E_MOCK) return 'e2e-published-version';
  const { data, error } = await client.rpc('publish_indicator', { p_local_id: localId, p_description: description, p_indicator_id: indicatorId ?? null });
  if (error) throw error;
  return String(data);
}

export async function checkIndicatorPublishSafety(localId: string, description: string, client: SupabaseClient = defaultClient): Promise<{ clean: boolean; violations: PublishCopyViolation[] }> {
  if (E2E_MOCK) return { clean: true, violations: [] };
  const { data, error } = await client.rpc('check_indicator_publish_safety', { p_local_id: localId, p_description: description });
  if (error) throw error;
  const result = data as { clean?: boolean; violations?: PublishCopyViolation[] } | null;
  return { clean: result?.clean === true, violations: Array.isArray(result?.violations) ? result.violations : [] };
}

export async function publishIndicatorWithoutUnsafeNotes(localId: string, description: string, indicatorId?: string, client: SupabaseClient = defaultClient): Promise<string> {
  if (E2E_MOCK) return 'e2e-published-version';
  const { data, error } = await client.rpc('publish_indicator_without_unsafe_notes', { p_local_id: localId, p_description: description, p_indicator_id: indicatorId ?? null });
  if (error) throw error;
  return String(data);
}

export async function installIndicatorVersion(versionId: string, client: SupabaseClient = defaultClient): Promise<string> {
  if (E2E_MOCK) return `mkt:${versionId.slice(0, 8)}`;
  const { data, error } = await client.rpc('install_indicator_version', { p_version_id: versionId });
  if (error) throw error;
  return String(data);
}

/**
 * 내가 게시한 지표 전량(공개 중 + 내린 것 + 운영자 차단분).
 * RLS `indicator_versions_author_read`(author_id = auth.uid())가 status 무관 SELECT를 허용하므로
 * 갤러리(public 한정)와 달리 본인 행은 전부 읽힌다.
 * 게시 슬롯 상한(20)은 status='public'인 DISTINCT indicator_id 기준이라 unlisted는 슬롯을 비운다.
 */
export async function fetchMyPublishedIndicators(userId: string, client: SupabaseClient = defaultClient): Promise<IndicatorGalleryItem[]> {
  if (E2E_MOCK) return [];
  const { data, error } = await client.from('indicator_versions').select(gallerySelect)
    .eq('author_id', userId).order('published_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as IndicatorGalleryItem[];
}

/** 게시 슬롯 소비량 — 상한 함수 indicator_publish_limit_reached와 같은 기준(공개 중 DISTINCT indicator_id). */
export function countPublishSlots(items: IndicatorGalleryItem[]): number {
  return new Set(items.filter((item) => item.status === 'public').map((item) => item.indicator_id)).size;
}

export async function setIndicatorStatus(versionId: string, status: 'public' | 'unlisted', client: SupabaseClient = defaultClient): Promise<void> {
  if (E2E_MOCK) return;
  const { error } = await client.rpc('set_indicator_status', { p_version_id: versionId, p_status: status });
  if (error) throw error;
}
